import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Send, Trash2, Users, Megaphone, Eye, EyeOff } from "lucide-react";
import {
  adminLogin,
  adminMe,
  adminListAnnouncements,
  adminCreateAnnouncement,
  adminUpdateAnnouncement,
  adminDeleteAnnouncement,
  adminPushCount,
  clearToken,
  getToken,
} from "@/lib/api";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await adminLogin(email.trim(), password);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-[28px] border border-white/15 bg-white/10 p-7 backdrop-blur-2xl"
        style={{ boxShadow: "0 24px 60px rgba(3,20,45,0.5)" }}
      >
        <div className="mb-6 flex flex-col items-center text-center">
          <img src="/eyb-logo.png" alt="EYB" className="h-14 w-14 object-contain" />
          <h1 className="font-heading mt-3 text-xl font-semibold text-white">
            EYB Admin
          </h1>
          <p className="text-xs text-white/50">Sign in to publish announcements</p>
        </div>

        <label className="mb-1 block text-xs text-white/60">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email"
          required
          className="mb-4 w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-[#2EC1F0]"
          placeholder="you@eybconsulting.com.au"
        />

        <label className="mb-1 block text-xs text-white/60">Password</label>
        <div className="relative mb-5">
          <input
            type={show ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password"
            required
            className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 pr-11 text-sm text-white outline-none focus:border-[#2EC1F0]"
            placeholder="••••••••"
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50"
            aria-label="Toggle password"
          >
            {show ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        </div>

        {error && (
          <p data-testid="admin-login-error" className="mb-3 text-sm text-red-300">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-btn"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#2EC1F0] to-[#1273C4] py-3 text-sm font-semibold text-white active:scale-[0.98] disabled:opacity-60"
        >
          {loading ? <Loader2 size={17} className="animate-spin" /> : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sendPush, setSendPush] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);

  const refresh = async () => {
    try {
      const [list, c] = await Promise.all([adminListAnnouncements(), adminPushCount()]);
      setItems(list);
      setCount(c);
    } catch {
      onLogout();
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const publish = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setPublishing(true);
    try {
      const res = await adminCreateAnnouncement({
        title: title.trim(),
        body: body.trim(),
        send_push: sendPush,
      });
      toast.success("Announcement published", {
        description: sendPush
          ? `Sent to ${res.push.sent} of ${res.push.attempted} device(s).`
          : "Saved without push.",
      });
      setTitle("");
      setBody("");
      refresh();
    } catch (err) {
      toast.error("Failed to publish", {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setPublishing(false);
    }
  };

  const toggle = async (item) => {
    await adminUpdateAnnouncement(item.id, { active: !item.active });
    refresh();
  };

  const remove = async (item) => {
    await adminDeleteAnnouncement(item.id);
    toast.success("Deleted");
    refresh();
  };

  return (
    <div className="mx-auto min-h-[100dvh] w-full max-w-lg px-5 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/eyb-logo.png" alt="EYB" className="h-9 w-9 object-contain" />
          <h1 className="font-heading text-xl font-semibold text-white">EYB Admin</h1>
        </div>
        <button
          type="button"
          onClick={onLogout}
          data-testid="admin-logout-btn"
          className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/80 active:scale-95"
        >
          <LogOut size={14} /> Logout
        </button>
      </div>

      <div className="mb-5 flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/75">
        <Users size={16} className="text-[#38D6B5]" />
        <span data-testid="subscriber-count">
          {count} device{count === 1 ? "" : "s"} subscribed to notifications
        </span>
      </div>

      {/* Compose */}
      <form
        onSubmit={publish}
        className="mb-8 rounded-[24px] border border-white/15 bg-white/10 p-5 backdrop-blur-xl"
      >
        <div className="mb-3 flex items-center gap-2">
          <Megaphone size={18} className="text-[#2EC1F0]" />
          <h2 className="font-heading font-semibold text-white">New announcement</h2>
        </div>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid="ann-title"
          maxLength={120}
          placeholder="Title"
          className="mb-3 w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-[#2EC1F0]"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          data-testid="ann-body"
          maxLength={1000}
          rows={4}
          placeholder="Write your message to all clients…"
          className="mb-3 w-full resize-none rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-[#2EC1F0]"
        />
        <label className="mb-4 flex cursor-pointer items-center gap-2 text-sm text-white/75">
          <input
            type="checkbox"
            checked={sendPush}
            onChange={(e) => setSendPush(e.target.checked)}
            data-testid="ann-sendpush"
            className="h-4 w-4 accent-[#2EC1F0]"
          />
          Also send a push notification to phones
        </label>
        <button
          type="submit"
          disabled={publishing || !title.trim() || !body.trim()}
          data-testid="ann-publish-btn"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#2EC1F0] to-[#1273C4] py-3 text-sm font-semibold text-white active:scale-[0.98] disabled:opacity-50"
        >
          {publishing ? (
            <Loader2 size={17} className="animate-spin" />
          ) : (
            <>
              <Send size={16} /> Publish
            </>
          )}
        </button>
      </form>

      {/* List */}
      <h2 className="mb-3 px-1 text-xs uppercase tracking-[0.25em] text-white/40">
        Published
      </h2>
      <div className="space-y-3">
        {items.length === 0 ? (
          <p className="py-8 text-center text-sm text-white/40">
            No announcements yet.
          </p>
        ) : (
          items.map((a) => (
            <div
              key={a.id}
              data-testid="admin-ann-item"
              className="rounded-2xl border border-white/10 bg-white/5 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-semibold text-white">{a.title}</h3>
                  <p className="mt-0.5 whitespace-pre-wrap text-sm text-white/65">
                    {a.body}
                  </p>
                  <p className="mt-1 text-[11px] text-white/35">{fmt(a.created_at)}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <button
                    type="button"
                    onClick={() => toggle(a)}
                    data-testid="admin-ann-toggle"
                    className={`rounded-full px-3 py-1 text-[11px] font-medium ${
                      a.active
                        ? "bg-[#38D6B5]/20 text-[#38D6B5]"
                        : "bg-white/10 text-white/50"
                    }`}
                  >
                    {a.active ? "Active" : "Hidden"}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(a)}
                    data-testid="admin-ann-delete"
                    aria-label="Delete"
                    className="text-white/40 hover:text-red-300"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    adminMe()
      .then(() => setAuthed(true))
      .catch(() => {
        clearToken();
        setAuthed(false);
      });
  }, []);

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center">
        <Loader2 className="animate-spin text-white/60" />
      </div>
    );
  }

  return authed ? (
    <Dashboard onLogout={logout} />
  ) : (
    <Login onSuccess={() => setAuthed(true)} />
  );
}
